import { jsPDF } from 'jspdf';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const generateQuotePDF = (quoteData, leadData) => {
  const doc = new jsPDF();
  
  // Colors
  const primaryColor = [37, 99, 235]; // Blue-600
  const textColor = [15, 23, 42]; // Slate-900
  const secondaryColor = [100, 116, 139]; // Slate-500
  
  // Helper functions
  const addText = (text, x, y, options = {}) => {
    const { fontSize = 12, color = textColor, align = 'left', bold = false } = options;
    doc.setFontSize(fontSize);
    doc.setTextColor(...color);
    if (bold) doc.setFont(undefined, 'bold');
    else doc.setFont(undefined, 'normal');
    doc.text(text, x, y, { align });
  };

  const addLine = (x1, y1, x2, y2, color = secondaryColor) => {
    doc.setDrawColor(...color);
    doc.line(x1, y1, x2, y2);
  };

  // Header Section
  let yPosition = 20;
  
  // Logo/Brand (Blue box with text)
  doc.setFillColor(...primaryColor);
  doc.rect(15, 15, 30, 12, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.text('QuoteSnapp', 30, 23, { align: 'center' });
  
  // Company tagline
  addText('Professional Quote Generation', 50, 23, { fontSize: 10, color: secondaryColor });
  
  yPosition = 40;
  
  // Title
  addText('PROJECT QUOTE', 15, yPosition, { fontSize: 24, color: primaryColor, bold: true });
  yPosition += 8;
  addLine(15, yPosition, 195, yPosition);
  yPosition += 10;
  
  // Customer Information
  if (leadData?.name || quoteData.customerName) {
    addText('Prepared For:', 15, yPosition, { fontSize: 10, color: secondaryColor });
    yPosition += 6;
    addText(leadData?.name || quoteData.customerName || 'Valued Customer', 15, yPosition, { fontSize: 14, bold: true });
    yPosition += 5;
  }
  
  if (leadData?.email) {
    addText(leadData.email, 15, yPosition, { fontSize: 10, color: secondaryColor });
    yPosition += 5;
  }
  
  if (leadData?.phone) {
    addText(leadData.phone, 15, yPosition, { fontSize: 10, color: secondaryColor });
    yPosition += 5;
  }
  
  if (leadData?.address) {
    addText(leadData.address, 15, yPosition, { fontSize: 10, color: secondaryColor });
    yPosition += 5;
  }
  
  yPosition += 5;
  
  // Date
  const date = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  addText(`Date: ${date}`, 15, yPosition, { fontSize: 10, color: secondaryColor });
  yPosition += 15;
  
  // Project Details Section
  addText('PROJECT DETAILS', 15, yPosition, { fontSize: 14, color: primaryColor, bold: true });
  yPosition += 8;
  addLine(15, yPosition, 195, yPosition, primaryColor);
  yPosition += 8;
  
  // Project info table
  const projectInfo = [
    ['Project Type:', quoteData.projectName],
    ['Area:', `${quoteData.squareFeet} sq ft`],
    ['Labor Hours:', `${quoteData.laborHours} hours`]
  ];
  
  projectInfo.forEach(([label, value]) => {
    addText(label, 15, yPosition, { fontSize: 11, color: secondaryColor });
    addText(value, 80, yPosition, { fontSize: 11, bold: true });
    yPosition += 6;
  });
  
  yPosition += 8;
  
  // Cost Breakdown Section
  addText('COST BREAKDOWN', 15, yPosition, { fontSize: 14, color: primaryColor, bold: true });
  yPosition += 8;
  addLine(15, yPosition, 195, yPosition, primaryColor);
  yPosition += 10;
  
  // Line items
  const lineItems = [
    ['Materials', `$${quoteData.materialCost}`],
    ['Labor', `$${quoteData.laborCost}`]
  ];
  
  if (parseFloat(quoteData.addOnsCost) > 0) {
    lineItems.push(['Add-ons', `$${quoteData.addOnsCost}`]);
  }
  
  lineItems.forEach(([item, amount]) => {
    addText(item, 15, yPosition, { fontSize: 11 });
    addText(amount, 195, yPosition, { fontSize: 11, align: 'right' });
    yPosition += 6;
  });
  
  yPosition += 3;
  addLine(15, yPosition, 195, yPosition);
  yPosition += 7;
  
  // Subtotal
  addText('Subtotal', 15, yPosition, { fontSize: 11, color: secondaryColor });
  addText(`$${quoteData.subtotal}`, 195, yPosition, { fontSize: 11, color: secondaryColor, align: 'right' });
  yPosition += 6;
  
  // Markup
  addText(`Markup (${quoteData.markupPercent}%)`, 15, yPosition, { fontSize: 11, color: secondaryColor });
  addText(`$${quoteData.markup}`, 195, yPosition, { fontSize: 11, color: secondaryColor, align: 'right' });
  yPosition += 8;
  
  // Total (highlighted)
  doc.setFillColor(240, 249, 255); // Light blue background
  doc.rect(13, yPosition - 5, 184, 10, 'F');
  
  addText('TOTAL', 15, yPosition, { fontSize: 14, bold: true });
  addText(`$${quoteData.total}`, 195, yPosition, { fontSize: 16, color: [34, 197, 94], bold: true, align: 'right' });
  yPosition += 15;
  
  // Notes section
  addText('NOTES', 15, yPosition, { fontSize: 12, color: primaryColor, bold: true });
  yPosition += 7;
  addLine(15, yPosition, 195, yPosition);
  yPosition += 7;
  
  const notes = [
    'This quote is valid for 30 days from the date above.',
    'Final pricing may vary based on site conditions.',
    'Payment terms: 50% deposit, 50% upon completion.'
  ];
  
  notes.forEach(note => {
    addText(`• ${note}`, 15, yPosition, { fontSize: 9, color: secondaryColor });
    yPosition += 5;
  });
  
  // Footer
  yPosition = 280;
  addLine(15, yPosition, 195, yPosition);
  yPosition += 5;
  addText('Generated by QuoteSnapp - Professional Quote Generation', 105, yPosition, { 
    fontSize: 8, 
    color: secondaryColor, 
    align: 'center' 
  });
  addText('Visit quotesnapp.com to create your own quotes instantly', 105, yPosition + 4, { 
    fontSize: 8, 
    color: secondaryColor, 
    align: 'center' 
  });
  
  // Generate filename
  const customerSlug = (leadData?.name || quoteData.customerName || 'customer')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-');
  const filename = `quote-${customerSlug}-${Date.now()}.pdf`;
  
  // Save the PDF
  doc.save(filename);
  
  // Track download event
  if (window.gtag) {
    window.gtag('event', 'pdf_download', {
      event_category: 'engagement',
      event_label: 'quote_pdf',
      value: parseFloat(quoteData.total)
    });
  }
  
  return filename;
};

// Store lead data - NOW USES REAL BACKEND API
export const storeLead = async (leadData, quoteData) => {
  console.log('Storing lead via API:', { leadData, quoteData });
  
  try {
    // Prepare data for backend
    const payload = {
      name: leadData.name,
      email: leadData.email,
      phone: leadData.phone || null,
      address: leadData.address || null,
      quote_data: {
        project_type: quoteData.projectType || 'interior-paint',
        project_name: quoteData.projectName,
        square_feet: parseFloat(quoteData.squareFeet) || 0,
        labor_rate: parseFloat(quoteData.laborRate) || 0,
        labor_hours: parseFloat(quoteData.laborHours) || 0,
        markup_percent: parseFloat(quoteData.markupPercent) || 0,
        material_cost: parseFloat(quoteData.materialCost) || 0,
        labor_cost: parseFloat(quoteData.laborCost) || 0,
        addons_cost: parseFloat(quoteData.addOnsCost) || 0,
        subtotal: parseFloat(quoteData.subtotal) || 0,
        markup: parseFloat(quoteData.markup) || 0,
        total: parseFloat(quoteData.total) || 0,
        customer_name: quoteData.customerName || leadData.name
      }
    };

    // Send to backend
    const response = await axios.post(`${API}/leads`, payload);
    
    console.log('Lead stored successfully:', response.data);
    
    // Also backup to localStorage
    try {
      const leads = JSON.parse(localStorage.getItem('quotesnapp_leads') || '[]');
      leads.push({
        ...leadData,
        quote: quoteData,
        timestamp: new Date().toISOString(),
        synced: true
      });
      localStorage.setItem('quotesnapp_leads', JSON.stringify(leads));
    } catch (localError) {
      console.warn('Could not backup to localStorage:', localError);
    }
    
    // Track lead event
    if (window.gtag) {
      window.gtag('event', 'generate_lead', {
        event_category: 'conversion',
        event_label: 'quote_lead',
        value: parseFloat(quoteData.total)
      });
    }
    
    return response.data;
  } catch (error) {
    console.error('Error storing lead:', error);
    
    // Fallback to localStorage if API fails
    try {
      const leads = JSON.parse(localStorage.getItem('quotesnapp_leads') || '[]');
      leads.push({
        ...leadData,
        quote: quoteData,
        timestamp: new Date().toISOString(),
        synced: false,
        error: error.message
      });
      localStorage.setItem('quotesnapp_leads', JSON.stringify(leads));
      console.log('Lead backed up to localStorage (API failed)');
    } catch (localError) {
      console.error('Failed to backup to localStorage:', localError);
    }
    
    throw error;
  }
};